import CircleByIdButton from '@/circle/components/CircleByIdButton'
import MemberByIdAvatar from '@/member/components/MemberByIdAvatar'
import {
  Avatar,
  Box,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  FlexProps,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { NextStepFragment, useUpdateNextStepMutation } from '@gql'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { NextStepItemIcon } from 'src/icons'

interface Props extends FlexProps {
  nextStep: NextStepFragment
  // Read-only rendering: no checkbox. Used in a thread, where a next step is
  // told, not resolved (G2-D8). Editing is another matter: correcting what one
  // wrote is not resolving it (C2, 10/08/2026).
  showRole?: boolean
  onEdit?(): void
  // Text only (G3-D11): correcting one's own wording is a private gesture,
  // while reassigning a step to another role or person is a circle decision
  // and belongs to the meeting.
  editableTitle?: boolean
  // Extra chips for the right-hand block, passed in rather than read here: only
  // the personal page shows the tension and the meeting (ENR06-D9), and only
  // its subscription asks for them. This component stays on NextStepFragment.
  chips?: React.ReactNode
}

// Truncating a title, on a line, is not the same as truncating a tension's:
// a tension carries a heading, a next step carries a COMMITMENT, and the end of
// the sentence is often the part that says what was promised. The tooltip is
// therefore not a nicety, it is what makes ENR06-D2 acceptable.
// `as const` and no Chakra props type: typing it as FlexProps would drag in
// `align` and `onChange`, which mean something else on Text and on Editable.
const titleEllipsis = {
  flex: 1,
  w: '0',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const

export default function NextStepItem({
  nextStep,
  showRole = true,
  onEdit,
  editableTitle,
  chips,
  ...flexProps
}: Props) {
  const [updateNextStep] = useUpdateNextStepMutation()
  const { t } = useTranslation()

  const doneStyle = {
    textDecoration: nextStep.done ? 'line-through' : undefined,
    color: nextStep.done ? 'gray.500' : undefined,
  }

  return (
    // One line, aligned on its middle, like a tension in the Tensions page
    // (ENR06-D1, D4). `align="center"` and not `flex-start`: with everything on
    // a single row there is no first line to hang from.
    <Flex align="center" gap={2} minW={0} {...flexProps}>
      <Box
        // Coloured like a tension's icon is, but on a different ground: a
        // tension's colour carries its STATUS (blue active, red blocked, green
        // closed), while purple here says the KIND of thing — it is free in
        // that palette, so no one can read a state into it (Yohan, 11/08/2026).
        // A finished step gives it up: the title is struck through and greyed,
        // a bright icon in front of it would fight the very thing it announces.
        color={nextStep.done ? 'gray.400' : 'purple.400'}
        _dark={{ color: nextStep.done ? 'gray.500' : 'purple.300' }}
        flexShrink={0}
        display="flex"
        alignItems="center"
      >
        {/* Size left to its default (24), the weight of a tension's icon
            (ENR06-D3, D16). It used to be pinned to 14 to sit above a second
            line that no longer exists. Outline stays: the filled footprint,
            repeated on every line, drew more attention than the steps. */}
        <NextStepItemIcon />
      </Box>

      {editableTitle ? (
        <Editable
          {...titleEllipsis}
          defaultValue={nextStep.title}
          onSubmit={(title) =>
            title.trim() &&
            title !== nextStep.title &&
            updateNextStep({
              variables: { id: nextStep.id, values: { title } },
            })
          }
        >
          {/* The tooltip sits on the PREVIEW, never on the whole Editable:
              hovering one's own text while typing it would be absurd. */}
          <Tooltip label={nextStep.title} openDelay={400}>
            <EditablePreview
              display="block"
              w="100%"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              {...doneStyle}
            />
          </Tooltip>
          {/* The input shows the whole text: one corrects what one can read. */}
          <EditableInput />
        </Editable>
      ) : (
        <Tooltip label={nextStep.title} openDelay={400}>
          <Text
            {...titleEllipsis}
            onClick={onEdit}
            cursor={onEdit ? 'pointer' : undefined}
            _hover={onEdit ? { textDecoration: 'underline' } : undefined}
            {...doneStyle}
            _dark={{ color: nextStep.done ? 'gray.400' : undefined }}
          >
            {nextStep.title}
          </Text>
        </Tooltip>
      )}

      {/* The right-hand block, pinned to a common edge — it is this vertical
          alignment, more than the single line, that makes the page readable at
          a glance. Member THEN role, copied from ThreadItem: taking a model is
          worth it only if the eye finds the same things in the same order from
          one page to the next (ENR06-D4). */}
      <Flex align="center" gap={2} flexShrink={0}>
        {nextStep.memberId ? (
          <MemberByIdAvatar id={nextStep.memberId} w={6} h={6} size="xs" />
        ) : (
          // A neutral silhouette rather than the words "nobody assigned": the
          // absence is still said out loud — it is not an oversight, the step
          // stays with the role until someone takes it — but it now occupies
          // exactly the width of an avatar, so the right column stays aligned.
          <Tooltip label={t('NextStepItem.unassigned')}>
            <Avatar w={6} h={6} size="xs" />
          </Tooltip>
        )}
        {showRole && <CircleByIdButton id={nextStep.circleId} size="xs" />}
        {chips}
      </Flex>
    </Flex>
  )
}
